/**
 * One place to turn a FastAPI error response into a sentence a person can act on.
 *
 * FastAPI's `detail` is a string for simple `HTTPException(status_code=..., detail="...")`
 * raises and a dict for the structured ones (missing attachments, duplicate filings), so
 * a caller that renders `detail` directly prints "[object Object]" exactly when the server
 * had the most to say. Several components had each grown their own copy of this reader;
 * this module is the shared one.
 */

/** Translate address terminology only in display text, never identifiers or routes. */
export const plainAddressMessage = (message) => typeof message === 'string'
  ? message.replace(/\ba situs(?: address| location)?\b/gi, (match) => /^[A-Z]/.test(match) ? 'An address' : 'an address')
    .replace(/\bsitus addresses\b/gi, 'addresses')
    .replace(/\bsitus address\b/gi, (match) => /^[A-Z]/.test(match) ? 'Address' : 'address')
    .replace(/\bsitus location\b/gi, (match) => /^[A-Z]/.test(match) ? 'Address' : 'address')
    .replace(/\bsitus\b/gi, (match) => /^[A-Z]/.test(match) ? 'Address' : 'address')
  : message;

/** Normalize only error-message fields; structured metadata remains intact. */
export function plainAddressError(payload) {
  if (Array.isArray(payload)) return payload.map(plainAddressError);
  if (!payload || typeof payload !== 'object') return payload;
  return Object.fromEntries(Object.entries(payload).map(([key, value]) => [key,
    ['detail', 'message', 'msg'].includes(key)
      ? (typeof value === 'string' ? plainAddressMessage(value) : plainAddressError(value))
      : value,
  ]));
}

/** Pull a human message out of FastAPI's `detail`, which may be a string or {message}. */
export const readDetailMessage = (detail, fallback) => {
  if (typeof detail === 'string' && detail) return plainAddressMessage(detail);
  if (detail && typeof detail.message === 'string' && detail.message) return plainAddressMessage(detail.message);
  return plainAddressMessage(fallback);
};

/**
 * Read a non-ok Response body once and produce the best available message.
 * Consumes the body, so call it at most once per Response.
 */
export async function readResponseError(res, fallback) {
  const payload = await res.json().catch(() => null);
  return readDetailMessage(payload?.detail, payload?.message || fallback);
}

/** Same, but keeps the structured detail for callers that render `missing[]` themselves. */
export async function readResponseErrorDetail(res, fallback) {
  const payload = await res.json().catch(() => null);
  return {
    message: readDetailMessage(payload?.detail, payload?.message || fallback),
    detail: payload?.detail ?? null,
    status: res.status,
  };
}
